import React, { useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

//Components
import { InputField } from "../formElements/InputField";
import { TextAreaField } from "../formElements/TextAreaField";
import { formatDateTime } from "../../common/Validations";
import {
  isGeneralTemplate,
  getGeneralTemplateSections,
  getGeneralQuestions,
} from "./pqGeneralTemplateQuestions";

const showDate = (value) => {
  if (!value) return "";
  return formatDateTime(value) || value;
};

const toDateInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const DisabledField = ({ label, name, value, type, xs = 12, md = 4 }) => (
  <Col xs={xs} md={md} lg={md} className="commTopButtonRightLeftPadding">
    <InputField
      className="inputBox"
      label={label}
      name={name}
      id={name}
      type={type || "text"}
      placeholder=""
      value={value || ""}
      disabled={true}
    />
  </Col>
);

const DisabledArea = ({ label, name, value }) => (
  <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
    <TextAreaField
      className="inputBox"
      label={label}
      name={name}
      id={name}
      value={value || ""}
      disabled={true}
      style={{ minHeight: "80px" }}
    />
  </Col>
);

const DisabledHalfArea = ({ label, name, value }) => (
  <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
    <TextAreaField
      className="inputBox"
      label={label}
      name={name}
      id={name}
      value={value || ""}
      disabled={true}
      style={{ minHeight: "80px" }}
    />
  </Col>
);

const baseSections = [
  { id: 1, key: "invitation", label: "Invitation", icon: "envelope" },
  { id: 2, key: "companyInformation", label: "Company Information", icon: "building" },
  { id: 3, key: "qualityManagement", label: "Quality Management", icon: "award" },
  { id: 4, key: "technicalCapability", label: "Technical Capability", icon: "cpu" },
  { id: 5, key: "deliveryCapability", label: "Delivery Capability", icon: "truck" },
  { id: 6, key: "complianceLegal", label: "Compliance & Legal", icon: "shield-check" },
  { id: 7, key: "financialInformation", label: "Financial Information", icon: "cash" },
  { id: 8, key: "commercialInformation", label: "Commercial Information", icon: "cart" },
  { id: 9, key: "experienceReferences", label: "Experience & References", icon: "briefcase" },
  { id: 10, key: "documentUpload", label: "Document Upload", icon: "file-earmark-arrow-up" },
  { id: 11, key: "declarationSubmit", label: "Declaration", icon: "check2-square" },
];

/* Read-only questionnaire tabs shared by the PQ submission page and the
 * PQ workflow details page. Pass `detail` from getPQVendorDetail; optional
 * `submission` / `invitation` override what is taken from detail. */
const PQQuestionnaireTabs = ({
  detail = {},
  submission: submissionProp,
  invitation: invitationProp,
}) => {
  const [activeSection, setActiveSection] = useState(1);

  const invitation = invitationProp || detail.invitation || {};
  const submission = submissionProp || detail.submission || {};

  const companyInfo = submission.companyInformation || {};
  const quality = submission.qualityManagement || {};
  const technical = submission.technicalCapability || {};
  const delivery = submission.deliveryCapability || {};
  const compliance = submission.complianceLegal || {};
  const financial = submission.financialInformation || {};
  const commercial = submission.commercialInformation || {};
  const experience = submission.experienceReferences || {};
  const declaration = submission.declaration || {};
  const documents = submission.documents || [];

  const statusValue =
    submission.status || invitation.status || detail.status || "Invited";

  const hasSubmission = Boolean(
    detail.hasSubmission ||
      submission.referenceNumber ||
      submission.status === "Submitted"
  );

  /* General template questions shown only when the invitation template name
   * starts with "General"; every other template keeps the existing tabs. */
  const isGeneral = isGeneralTemplate(
    invitation.template || detail.template
  );

  /* General template: only the template sheet's own sections are shown (the
   * app-only Invitation / Document Upload / Declaration tabs are dropped).
   * Every other template keeps the existing baseSections unchanged. */
  const sections = isGeneral ? getGeneralTemplateSections() : baseSections;

  const activeSectionIndex = sections.findIndex(
    (s) => s.id === activeSection
  );

  const handlePrevious = () => {
    if (activeSectionIndex > 0) {
      setActiveSection(sections[activeSectionIndex - 1].id);
    }
  };
  const handleNext = () => {
    if (activeSectionIndex > -1 && activeSectionIndex < sections.length - 1) {
      setActiveSection(sections[activeSectionIndex + 1].id);
    }
  };

  const noDataMsg = (
    <p className="subText mb-0">Assessment has not been submitted yet.</p>
  );

  const renderInvitation = () => (
    <Row className="pt-2 pb-4">
      <DisabledField
        label="Invitation ID"
        name="invitationId"
        value={
          invitation.invitationId || invitation.userId || detail.supplierId
        }
      />
      <DisabledField
        label="Company Name"
        name="invCompanyName"
        value={
          invitation.companyName || companyInfo.companyName || detail.companyName
        }
      />
      <DisabledField
        label="Email"
        name="invEmail"
        value={invitation.email || detail.email}
      />
      <DisabledField
        label="Category"
        name="invCategory"
        value={invitation.category || detail.category}
      />
      <DisabledField
        label="Sub Category"
        name="invSubCategory"
        value={invitation.subCategory || detail.subCategory}
      />
      <DisabledField
        label="Template"
        name="invTemplate"
        value={invitation.template || detail.template}
      />
      <DisabledField
        label="Submission Deadline"
        name="invDeadline"
        value={showDate(
          invitation.submissionDeadline || detail.submissionDeadline
        )}
      />
      <DisabledField
        label="Invited By"
        name="invitedBy"
        value={
          invitation.invitedByName ||
          invitation.invitedBy ||
          detail.invitedByName ||
          detail.invitedBy
        }
      />
      <DisabledField
        label="Invited At"
        name="invitedAt"
        value={showDate(invitation.createdAt || detail.createdAt)}
      />
      <DisabledField
        label="Status"
        name="invStatus"
        value={statusValue}
      />
      <DisabledArea
        label="Invitation Message"
        name="invMessage"
        value={invitation.message || detail.message}
      />
    </Row>
  );

  const renderCompanyInformation = () =>
    !hasSubmission ? (
      noDataMsg
    ) : (
      <Row className="pt-2 pb-4">
        <DisabledField
          label="Reference Number"
          name="refNo"
          value={submission.referenceNumber}
        />
        <DisabledField
          label="Company Name"
          name="subCompanyName"
          value={companyInfo.companyName}
        />
        <DisabledField
          label="Email"
          name="subEmail"
          value={companyInfo.email}
        />
        <DisabledField
          label="Year of Establishment"
          name="yearOfEstablishment"
          value={companyInfo.yearOfEstablishment}
          type="number"
        />
        <DisabledField
          label="Company Type"
          name="companyType"
          value={companyInfo.companyType}
        />
        <DisabledField
          label="PAN Number"
          name="panNumber"
          value={companyInfo.panNumber}
        />
        <DisabledField
          label="GST Number"
          name="gstNumber"
          value={companyInfo.gstNumber}
        />
        <DisabledField
          label="Website"
          name="website"
          value={companyInfo.website}
        />
        <DisabledField
          label="Submitted At"
          name="submittedAt"
          value={showDate(submission.createdAt)}
        />
      </Row>
    );

  const renderQualityManagement = () =>
    !hasSubmission ? (
      noDataMsg
    ) : (
      <Row className="pt-2 pb-4">
        <DisabledField
          label="ISO 9001 Certified?"
          name="iso9001"
          value={quality.iso9001}
        />
        <DisabledField
          label="ISO Certificate Number"
          name="isoCertificateNumber"
          value={quality.isoCertificateNumber}
        />
        <DisabledField
          label="Regular Quality Inspection?"
          name="qualityInspection"
          value={quality.qualityInspection}
        />
        <DisabledField
          label="Defect Rate (%)"
          name="defectRate"
          value={quality.defectRate}
          type="number"
        />
        <DisabledArea
          label="Quality Policy"
          name="qualityPolicy"
          value={quality.qualityPolicy}
        />
        <DisabledArea
          label="Quality Remarks"
          name="qualityRemarks"
          value={quality.qualityRemarks}
        />
      </Row>
    );

  const renderTechnicalCapability = () =>
    !hasSubmission ? (
      noDataMsg
    ) : (
      <Row className="pt-2 pb-4">
        <DisabledHalfArea
          label="Machinery & Equipment"
          name="machineryEquipment"
          value={technical.machineryEquipment}
        />
        <DisabledField
          label="Production Capacity"
          name="productionCapacity"
          value={technical.productionCapacity}
        />
        <DisabledField
          label="Technical Team Strength"
          name="technicalTeamStrength"
          value={technical.technicalTeamStrength}
          type="number"
        />
        <DisabledField
          label="In-house Design Capability?"
          name="designCapability"
          value={technical.designCapability}
        />
        <DisabledArea
          label="Technical Remarks"
          name="technicalRemarks"
          value={technical.technicalRemarks}
        />
      </Row>
    );

  const renderDeliveryCapability = () =>
    !hasSubmission ? (
      noDataMsg
    ) : (
      <Row className="pt-2 pb-4">
        <DisabledHalfArea
          label="Delivery Locations"
          name="deliveryLocations"
          value={delivery.deliveryLocations}
        />
        <DisabledField
          label="Average Lead Time (Days)"
          name="averageLeadTime"
          value={delivery.averageLeadTime}
          type="number"
        />
        <DisabledField
          label="Warehouse Capacity"
          name="warehouseCapacity"
          value={delivery.warehouseCapacity}
        />
        <DisabledField
          label="Logistics Partner"
          name="logisticsPartner"
          value={delivery.logisticsPartner}
        />
        <DisabledArea
          label="Delivery Remarks"
          name="deliveryRemarks"
          value={delivery.deliveryRemarks}
        />
      </Row>
    );

  const renderComplianceLegal = () =>
    !hasSubmission ? (
      noDataMsg
    ) : (
      <Row className="pt-2 pb-4">
        <DisabledField
          label="GST Registered?"
          name="gstRegistered"
          value={compliance.gstRegistered}
        />
        <DisabledField
          label="Trade License Number"
          name="tradeLicense"
          value={compliance.tradeLicense}
        />
        <DisabledField
          label="Environmental Clearance"
          name="environmentalClearance"
          value={compliance.environmentalClearance}
        />
        <DisabledField
          label="Labour Compliance Status"
          name="labourCompliance"
          value={compliance.labourCompliance}
        />
        <DisabledArea
          label="Compliance Remarks"
          name="complianceRemarks"
          value={compliance.complianceRemarks}
        />
      </Row>
    );

  const renderFinancialInformation = () =>
    !hasSubmission ? (
      noDataMsg
    ) : (
      <Row className="pt-2 pb-4">
        <DisabledField
          label="Annual Turnover"
          name="annualTurnover"
          value={financial.annualTurnover}
        />
        <DisabledField
          label="Bank Name"
          name="bankName"
          value={financial.bankName}
        />
        <DisabledField
          label="Bank Account Number"
          name="bankAccountNumber"
          value={financial.bankAccountNumber}
        />
        <DisabledField
          label="Credit Rating"
          name="creditRating"
          value={financial.creditRating}
        />
        <DisabledField
          label="IFSC Code"
          name="ifscCode"
          value={financial.ifscCode}
        />
        <DisabledArea
          label="Financial Remarks"
          name="financialRemarks"
          value={financial.financialRemarks}
        />
      </Row>
    );

  const renderCommercialInformation = () =>
    !hasSubmission ? (
      noDataMsg
    ) : (
      <Row className="pt-2 pb-4">
        <DisabledField
          label="Payment Terms"
          name="paymentTerms"
          value={commercial.paymentTerms}
        />
        <DisabledField
          label="Currency"
          name="currency"
          value={commercial.currency}
        />
        <DisabledField
          label="Price List Reference"
          name="priceList"
          value={commercial.priceList}
        />
        <DisabledField
          label="Warranty Terms"
          name="warrantyTerms"
          value={commercial.warrantyTerms}
        />
        <DisabledArea
          label="Commercial Remarks"
          name="commercialRemarks"
          value={commercial.commercialRemarks}
        />
      </Row>
    );

  const renderExperienceReferences = () =>
    !hasSubmission ? (
      noDataMsg
    ) : (
      <Row className="pt-2 pb-4">
        <DisabledField
          label="Years of Experience"
          name="yearsOfExperience"
          value={experience.yearsOfExperience}
          type="number"
        />
        <DisabledHalfArea
          label="Major Clients"
          name="majorClients"
          value={experience.majorClients}
        />
        <DisabledField
          label="Reference Contact Person"
          name="referenceContact"
          value={experience.referenceContact}
        />
        <DisabledField
          label="Reference Email"
          name="referenceEmail"
          value={experience.referenceEmail}
        />
        <DisabledArea
          label="Experience Remarks"
          name="experienceRemarks"
          value={experience.experienceRemarks}
        />
      </Row>
    );

  const renderDocumentUpload = () => {
    if (!hasSubmission) return noDataMsg;
    if (!documents.length)
      return <p className="subText mb-0">No documents uploaded.</p>;

    return (
      <div className="d-flex-column justify-content-center">
        <Row className="pt-4">
          <Col xs={12} md={4} lg={2}>
            <p className="fs-6 fw-medium d-flex justify-content-center">
              Document Type
            </p>
          </Col>
          <Col xs={12} md={4} lg={2}>
            <p className="fs-6 fw-medium d-flex ms-4">Document Number</p>
          </Col>
          <Col xs={12} md={4} lg={2}>
            <p className="fs-6 fw-medium d-flex ms-4">Expiration Date</p>
          </Col>
          <Col xs={12} md={4} lg={1}>
            <p className="fs-6 fw-medium d-flex ms-4">View Files</p>
          </Col>
          <Col xs={12} md={4} lg={4}>
            <p className="fs-6 fw-medium d-flex ms-4">Attachments</p>
          </Col>
          <Col xs={12} md={4} lg={1}>
            <p className="fs-6 fw-medium d-flex justify-content-center">
              Action
            </p>
          </Col>
        </Row>
        {documents.map((doc, index) => (
          <Row key={doc._id || doc.id || index}>
            <Col xs={12}>
              <hr className="mx-4 my-0 text-color-black" />
            </Col>
            <Col
              xs={12}
              md={4}
              lg={2}
              className="commTopButtonRightLeftPadding"
            >
              <InputField
                className="inputBox"
                label=""
                placeholder="Document type"
                name="documentType"
                value={doc.documentType || ""}
                disabled={true}
              />
            </Col>
            <Col
              xs={12}
              md={4}
              lg={2}
              className="commTopButtonRightLeftPadding"
            >
              <InputField
                className="inputBox"
                label=""
                placeholder="Document Number"
                name="documentNumber"
                value={doc.documentNumber || ""}
                disabled={true}
              />
            </Col>
            <Col
              xs={12}
              md={4}
              lg={2}
              className="commTopButtonRightLeftPadding"
            >
              <InputField
                className="inputBox"
                label=""
                type="date"
                name="expirationDate"
                value={toDateInput(doc.expirationDate)}
                disabled={true}
              />
            </Col>
            <Col
              xs={12}
              md={4}
              lg={1}
              className="commTopButtonRightLeftPadding"
            >
              <div className="d-flex justify-content-center">
                {doc.attachmentId ? (
                  <img
                    src="assets/img/newIcons/attached-file.png"
                    className="mt-1 attachmentIcon"
                    alt="attachments"
                  />
                ) : (
                  "--"
                )}
              </div>
            </Col>
            <Col
              xs={12}
              md={4}
              lg={4}
              className="commTopButtonRightLeftPadding"
            >
              <div className="d-flex justify-content-center">
                {doc.attachmentId ? (
                  <span className="badge bg-table-success-status">
                    <i className="bi bi-paperclip me-1"></i> Attached
                  </span>
                ) : (
                  "--"
                )}
              </div>
            </Col>
            <Col
              xs={12}
              md={4}
              lg={1}
              className="commTopButtonRightLeftPadding"
            >
              <div className="d-flex justify-content-center">
                <span className="text-muted">--</span>
              </div>
            </Col>
          </Row>
        ))}
      </div>
    );
  };

  const renderDeclaration = () =>
    !hasSubmission ? (
      noDataMsg
    ) : (
      <Row className="pt-2 pb-4">
        <Col
          xs={12}
          md={12}
          lg={12}
          className="commTopButtonRightLeftPadding"
        >
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="declarationCheckWorkflow"
              checked={
                declaration.declared === true ||
                declaration.declared === "true"
              }
              disabled={true}
            />
            <label className="form-check-label" htmlFor="declarationCheckWorkflow">
              I declare that the information provided above is true and correct
              to the best of my knowledge.
            </label>
          </div>
        </Col>
        <DisabledField
          label="Authorised Person Name"
          name="authorisedPerson"
          value={declaration.authorisedPerson}
        />
        <DisabledField
          label="Designation"
          name="designation"
          value={declaration.designation}
        />
        <DisabledField
          label="Remarks"
          name="declarationRemarks"
          value={declaration.remarks}
        />
        <DisabledField
          label="Submitted By"
          name="submittedBy"
          value={submission.submittedBy || detail.submittedBy}
        />
        <DisabledField
          label="Submitted At"
          name="submittedAtDecl"
          value={showDate(submission.createdAt || detail.submittedAt)}
        />
      </Row>
    );

  /* ---- General template questions (read-only) ---- */
  const renderGeneralFields = (sectionKey) => {
    const values = submission[sectionKey] || {};
    return getGeneralQuestions(sectionKey).map((q) => {
      const numeric = ["number", "year", "currency", "percent"].includes(
        q.type
      );
      const wide = ["textarea", "table", "multi"].includes(q.type);

      if (q.type === "yesno_details") {
        return (
          <React.Fragment key={q.name}>
            <DisabledField
              label={q.label}
              name={q.name}
              value={values[q.name]}
            />
            <DisabledField
              label={`${q.label} - Details`}
              name={`${q.name}Details`}
              value={values[`${q.name}Details`]}
            />
          </React.Fragment>
        );
      }

      if (q.type === "upload") {
        return (
          <DisabledField
            key={q.name}
            label={q.label}
            name={q.name}
            xs={12}
            md={4}
            value={values[q.name] ? "Attached" : ""}
          />
        );
      }

      if (q.type === "auto_years") {
        const estYear =
          values.yearEstablished || companyInfo.yearOfEstablishment || "";
        return (
          <DisabledField
            key={q.name}
            label={q.label}
            name={q.name}
            value={
              estYear ? String(new Date().getFullYear() - Number(estYear)) : ""
            }
            type="number"
          />
        );
      }

      if (wide) {
        return (
          <Col
            key={q.name}
            xs={12}
            md={4}
            lg={4}
            className="commTopButtonRightLeftPadding"
          >
            <TextAreaField
              className="inputBox"
              label={q.label}
              name={q.name}
              id={q.name}
              value={values[q.name] || ""}
              disabled={true}
              style={{ minHeight: "80px" }}
            />
          </Col>
        );
      }

      return (
        <DisabledField
          key={q.name}
          label={q.label}
          name={q.name}
          value={values[q.name]}
          type={numeric ? "number" : "text"}
        />
      );
    });
  };

  const withGeneral = (sectionKey, content) => (
    <>
      {content}
      {isGeneral &&
        hasSubmission &&
        getGeneralQuestions(sectionKey).length > 0 && (
          <Row className="pt-2 pb-4">{renderGeneralFields(sectionKey)}</Row>
        )}
    </>
  );

  const renderGeneralOnly = (sectionKey) =>
    !hasSubmission ? (
      noDataMsg
    ) : (
      <Row className="pt-2 pb-4">{renderGeneralFields(sectionKey)}</Row>
    );

  const renderSectionContent = () => {
    const sectionKey = sections.find((s) => s.id === activeSection)?.key;
    switch (sectionKey) {
      case "invitation":
        return renderInvitation();
      case "companyInformation":
        return withGeneral("companyInformation", renderCompanyInformation());
      case "qualityManagement":
        return withGeneral("qualityManagement", renderQualityManagement());
      case "technicalCapability":
        return withGeneral("technicalCapability", renderTechnicalCapability());
      case "deliveryCapability":
        return withGeneral("deliveryCapability", renderDeliveryCapability());
      case "complianceLegal":
        return withGeneral("complianceLegal", renderComplianceLegal());
      case "financialInformation":
        /* General template carries the bank / financial fields in its question
         * config, other templates use the static block above. */
        return isGeneral
          ? renderGeneralOnly("financialInformation")
          : renderFinancialInformation();
      case "commercialInformation":
        return withGeneral("commercialInformation", renderCommercialInformation());
      case "experienceReferences":
        return withGeneral("experienceReferences", renderExperienceReferences());
      case "documentUpload":
        return renderDocumentUpload();
      case "declarationSubmit":
        return renderDeclaration();
      case "customerServiceSupport":
        return renderGeneralOnly("customerServiceSupport");
      case "riskCompliance":
        return renderGeneralOnly("riskCompliance");
      default:
        return null;
    }
  };

  const activeSectionObj = sections.find((s) => s.id === activeSection);

  return (
    <div className="card">
      <div className="card-body">
        <ul className="nav nav-tabs" id="pqQuestionnaireTab" role="tablist">
          {sections.map((section, sectionIdx) => {
            const isActive = activeSection === section.id;
            return (
              <li
                className="nav-item"
                role="presentation"
                key={section.id}
              >
                <button
                  className={`nav-link ${isActive ? "active" : ""}`}
                  id={`qsection-${section.id}-tab`}
                  type="button"
                  role="tab"
                  aria-controls={`qsection-${section.id}`}
                  aria-selected={isActive ? "true" : "false"}
                  onClick={() => setActiveSection(section.id)}
                >
                  <span className="me-1">{sectionIdx + 1}.</span>
                  {section.label}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="tab-content pt-3" id="pqQuestionnaireTabContent">
          <div
            className="tab-pane fade show active"
            id={`qsection-${activeSection}`}
            role="tabpanel"
            aria-labelledby={`qsection-${activeSection}-tab`}
          >
            <div className="card">
              <div className="card-header bg-white">
                <strong>
                  {activeSectionIndex + 1}. {activeSectionObj?.label}
                </strong>
              </div>
              <div className="card-body">{renderSectionContent()}</div>
              <div className="card-footer bg-white">
                <Row className="py-2">
                  <Col
                    xs={12}
                    md={2}
                    lg={2}
                    className="commTopButtonRightLeftPadding"
                  >
                    <button
                      className="btnTable"
                      onClick={handlePrevious}
                      disabled={activeSectionIndex <= 0}
                    >
                      <i className="bi bi-arrow-left"></i> Previous
                    </button>
                  </Col>
                  <Col
                    xs={12}
                    md={{ span: 2, offset: 10 }}
                    lg={{ span: 2, offset: 10 }}
                    className="commTopButtonRightLeftPadding"
                  >
                    <button
                      className="btnTable"
                      onClick={handleNext}
                      disabled={activeSectionIndex >= sections.length - 1}
                    >
                      Next <i className="bi bi-arrow-right"></i>
                    </button>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PQQuestionnaireTabs;
